import type { PassDto, VisitDto } from '@aawas/contracts';
import { del, get, set } from 'idb-keyval';
import { useSyncExternalStore } from 'react';
import { api, ApiError } from './api';

/**
 * Guard offline support.
 *  - `snapshot`: flats, resident contacts and active pass codes, cached for use without a network.
 *  - `outbox`: gate actions performed while offline. Every one carries an idempotency key (or is a
 *    naturally idempotent state change), so replaying after a flaky reconnect never duplicates a visit.
 */

export interface Snapshot {
  generatedAt: string;
  units: Array<{ id: string; label: string; residents: Array<{ name: string | null; phone: string }> }>;
  passes: Array<{ id: string; code: string; unitId: string; visitorName: string; validFrom: string; validUntil: string; usesLeft: number }>;
  settings: { approvalTimeoutSec: number; noResponsePolicy: string };
}

export type OutboxItem =
  | { id: string; kind: 'visit'; body: Record<string, unknown>; label: string; at: number }
  | { id: string; kind: 'checkin'; body: Record<string, unknown>; label: string; at: number }
  | { id: string; kind: 'enter' | 'exit' | 'cancel'; visitId: string; label: string; at: number };

const SNAP_KEY = 'gate.snapshot';
const BOX_KEY = 'gate.outbox';

let outbox: OutboxItem[] = [];
let loaded = false;
const subs = new Set<() => void>();
const emit = () => subs.forEach((s) => s());
let version = 0;

async function load() {
  if (loaded) return;
  outbox = (await get<OutboxItem[]>(BOX_KEY)) ?? [];
  loaded = true;
  version++;
  emit();
}
async function persist() {
  await set(BOX_KEY, outbox);
  version++;
  emit();
}

export const newKey = () => `${crypto.randomUUID()}`;

export function useOutbox(): OutboxItem[] {
  useSyncExternalStore(
    (l) => {
      subs.add(l);
      void load();
      return () => subs.delete(l);
    },
    () => version,
  );
  return outbox;
}

export async function loadSnapshot(): Promise<Snapshot | undefined> {
  return get<Snapshot>(SNAP_KEY);
}
export async function refreshSnapshot(): Promise<Snapshot | undefined> {
  try {
    const s = await api<Snapshot>('/gate/snapshot');
    await set(SNAP_KEY, s);
    return s;
  } catch {
    return loadSnapshot();
  }
}
export const clearGateCache = () => Promise.all([del(SNAP_KEY), del(BOX_KEY)]);

/** Checks a pass code against the cached snapshot (used only when the server is unreachable). */
export function verifyPassOffline(snap: Snapshot | undefined, code: string): (Snapshot['passes'][number] & { unitLabel: string }) | null {
  if (!snap) return null;
  const now = Date.now();
  const p = snap.passes.find((x) => x.code === code && x.usesLeft > 0 && new Date(x.validFrom).getTime() <= now && new Date(x.validUntil).getTime() >= now);
  if (!p) return null;
  return { ...p, unitLabel: snap.units.find((u) => u.id === p.unitId)?.label ?? '' };
}

export async function enqueue(item: OutboxItem) {
  await load();
  outbox = [...outbox, item];
  await persist();
}

let flushing = false;
export interface FlushResult {
  sent: number;
  dropped: Array<{ label: string; reason: string }>;
}

/** Replays queued actions in order. Stops at the first network failure; drops actions the server rejects. */
export async function flushOutbox(): Promise<FlushResult> {
  const result: FlushResult = { sent: 0, dropped: [] };
  await load();
  if (flushing || outbox.length === 0) return result;
  flushing = true;
  try {
    while (outbox.length) {
      const item = outbox[0]!;
      try {
        if (item.kind === 'visit') await api<VisitDto>('/visits', { body: item.body });
        else if (item.kind === 'checkin') await api<VisitDto>('/passes/check-in', { body: item.body });
        else await api<VisitDto>(`/visits/${item.visitId}/${item.kind}`, { body: {} });
        result.sent++;
      } catch (e) {
        if (e instanceof ApiError && e.isNetwork) break; // still offline: keep everything
        if (e instanceof ApiError && e.status >= 500) break; // server trouble: retry later
        result.dropped.push({ label: item.label, reason: e instanceof ApiError ? e.message : 'Failed' });
      }
      outbox = outbox.slice(1);
      await persist();
    }
  } finally {
    flushing = false;
  }
  return result;
}

export type { PassDto };
